import { FilterState, ApiFilterResponse } from '@interfaces/filter.types';

export interface ApiFilterParams {
  bodyTypes?: string[];
  segments?: string[];
  brand?: string[];
  modelIds?: number[];
  minPrice?: string;
  maxPrice?: string;
  fuelTypes?: string[];
  allExpressDelivery?: boolean;
  sortBy?: string;
  transmissions?: string[];
  seatCapacity?: string[];
  slug?: string;
}

export const transformFiltersToApiParams = (
  filters: FilterState, 
  filterOptions: ApiFilterResponse | null
): ApiFilterParams => {
  const apiParams: ApiFilterParams = {};

  if (filters.appliedFilters.body_type && filters.appliedFilters.body_type.length > 0) {
    const bodyTypes: string[] = [];
    const segments: string[] = [];
    
    filters.appliedFilters.body_type.forEach(selectedItem => {
      if (filterOptions?.body_type) {
        const parentBodyType = filterOptions.body_type.find(bt => 
          bt.value === selectedItem || bt.key === selectedItem
        );
        
        if (parentBodyType) {
          bodyTypes.push(selectedItem);
        } else {
          const isSubcategory = filterOptions.body_type.some(bt =>
            bt.sub_values?.some(sub => sub.value === selectedItem || sub.key === selectedItem)
          );
          
          if (isSubcategory) {
            segments.push(selectedItem);
          }
        }
      } else {
        bodyTypes.push(selectedItem);
      }
    });

    if (bodyTypes.length > 0) {
      apiParams.bodyTypes = bodyTypes;
    }
    if (segments.length > 0) {
      apiParams.segments = segments;
    }
  }

  if (filters.appliedFilters.brand_model && filters.appliedFilters.brand_model.length > 0) {
    const brandNames: string[] = [];
    
    filters.appliedFilters.brand_model.forEach(selectedItem => {
      if (filterOptions?.brand_model) {
        const brand = filterOptions.brand_model.find((b: any) => b.brand_name === selectedItem);
        if (brand) {
          if (!brandNames.includes(selectedItem)) {
            brandNames.push(selectedItem);
          }
        } else {
          const parentBrand = filterOptions.brand_model.find((b: any) =>
            b.models?.some((m: any) => m.model_name === selectedItem)
          );
          if (parentBrand && !brandNames.includes(parentBrand.brand_name)) {
            brandNames.push(parentBrand.brand_name);
          }
        }
      } else {
        if (!brandNames.includes(selectedItem)) {
          brandNames.push(selectedItem);
        }
      }
    });

    if (brandNames.length > 0) {
      apiParams.brand = brandNames;
    }
  }

  if (filters.appliedFilters.budget) {
    apiParams.minPrice = filters.appliedFilters.budget.min.toString();
    apiParams.maxPrice = filters.appliedFilters.budget.max.toString();
  }

  if (filters.appliedFilters.fuel_type && filters.appliedFilters.fuel_type.length > 0) {
    apiParams.fuelTypes = filters.appliedFilters.fuel_type;
  }

  if (filters.appliedFilters.is_express_delivery !== undefined) {
    apiParams.allExpressDelivery = filters.appliedFilters.is_express_delivery;
  }

  if (filters.appliedFilters.sort_by && filters.appliedFilters.sort_by.length > 0) {
    apiParams.sortBy = filters.appliedFilters.sort_by[0];
  }

  if (filters.appliedFilters.transmission && filters.appliedFilters.transmission.length > 0) {
    apiParams.transmissions = filters.appliedFilters.transmission;
  }

  if (filters.appliedFilters.seat_capacity && filters.appliedFilters.seat_capacity.length > 0) {
    apiParams.seatCapacity = filters.appliedFilters.seat_capacity;
  }

  return apiParams;
};


export const buildQueryString = (
  params: ApiFilterParams, 
  baseParams: Record<string, string | number> = {}
): string => {
  const queryParams = new URLSearchParams();

  Object.entries(baseParams).forEach(([key, value]) => {
    queryParams.append(key, value.toString());
  });

  if (params.bodyTypes && params.bodyTypes.length > 0) {
    params.bodyTypes.forEach(bodyType => {
      queryParams.append('bodyTypes', bodyType);
    });
  }

  if (params.segments && params.segments.length > 0) {
    params.segments.forEach(segment => {
      queryParams.append('segments', segment);
    });
  }

  if (params.brand && params.brand.length > 0) {
    params.brand.forEach(brand => {
      queryParams.append('brand', brand);
    });
  }

  if (params.modelIds && params.modelIds.length > 0) {
    params.modelIds.forEach(modelId => {
      queryParams.append('modelIds', modelId.toString());
    });
  }

  if (params.minPrice !== undefined) {
    queryParams.append('minPrice', params.minPrice);
  }
  if (params.maxPrice !== undefined) {
    queryParams.append('maxPrice', params.maxPrice);
  }

  if (params.fuelTypes && params.fuelTypes.length > 0) {
    params.fuelTypes.forEach(fuel => {
      queryParams.append('fuelTypes', fuel);
    });
  }

  if (params.allExpressDelivery !== undefined) {
    queryParams.append('allExpressDelivery', params.allExpressDelivery.toString());
  }

  if (params.sortBy) {
    queryParams.append('sortBy', params.sortBy);
  }

  if (params.transmissions && params.transmissions.length > 0) {
    params.transmissions.forEach(transmission => {
      queryParams.append('transmissions', transmission);
    });
  }

  if (params.seatCapacity && params.seatCapacity.length > 0) {
    params.seatCapacity.forEach(capacity => {
      queryParams.append('seatCapacity', capacity);
    });
  }

  let queryString = queryParams.toString();
  
  if (params.slug) {
    const slugParam = `slug=${params.slug}`;
    queryString = queryString ? `${queryString}&${slugParam}` : slugParam;
  }

  return queryString;
};


export const buildUrlFromFilters = (
  appliedFilters: FilterState['appliedFilters'], 
  filterOptions: ApiFilterResponse | null,
  budgetDefaults?: { min: number; max: number }
): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (appliedFilters.body_type && appliedFilters.body_type.length > 0) {
    const bodyTypes: string[] = [];
    const segments: string[] = [];
    
    appliedFilters.body_type.forEach(selectedItem => {
      if (filterOptions?.body_type) {
        const parentBodyType = filterOptions.body_type.find(bt => 
          bt.value === selectedItem || bt.key === selectedItem
        );
        
        if (parentBodyType) {
          bodyTypes.push(selectedItem);
        } else {
          const isSubcategory = filterOptions.body_type.some(bt =>
            bt.sub_values?.some(sub => sub.value === selectedItem || sub.key === selectedItem)
          );
          
          if (isSubcategory) {
            segments.push(selectedItem);
          }
        }
      } else {
        bodyTypes.push(selectedItem);
      }
    });

    bodyTypes.forEach(type => searchParams.append('bodyTypes', type));
    segments.forEach(segment => searchParams.append('segments', segment));
  }

  if (appliedFilters.brand_model && appliedFilters.brand_model.length > 0) {
    appliedFilters.brand_model.forEach(model => {
      searchParams.append('brand', model);
    });
  }

  if (appliedFilters.fuel_type && appliedFilters.fuel_type.length > 0) {
    appliedFilters.fuel_type.forEach(fuel => {
      searchParams.append('fuelTypes', fuel);
    });
  }

  if (appliedFilters.transmission && appliedFilters.transmission.length > 0) {
    appliedFilters.transmission.forEach(trans => {
      searchParams.append('transmissions', trans);
    });
  }

  if (appliedFilters.budget && budgetDefaults) {
    const { min, max } = appliedFilters.budget;
    if (min !== budgetDefaults.min || max !== budgetDefaults.max) {
      searchParams.append('minPrice', min.toString());
      searchParams.append('maxPrice', max.toString());
    }
  }

  if (appliedFilters.is_express_delivery !== undefined) {
    searchParams.append('isExpressDelivery', appliedFilters.is_express_delivery.toString());
  }

  if (appliedFilters.feature && appliedFilters.feature.length > 0) {
    appliedFilters.feature.forEach(feature => {
      searchParams.append('features', feature);
    });
  }

  if (appliedFilters.sort_by && appliedFilters.sort_by.length > 0) {
    appliedFilters.sort_by.forEach(sort => {
      searchParams.append('sortBy', sort);
    });
  }

  if (appliedFilters.seat_capacity && appliedFilters.seat_capacity.length > 0) {
    appliedFilters.seat_capacity.forEach(capacity => {
      searchParams.append('seatCapacity', capacity);
    });
  }

  return searchParams;
};

export const encodeUrlWithSpaces = (urlSearchParams: URLSearchParams): string => {
  return urlSearchParams.toString().replace(/\+/g, '%20');
}; 